import React, { useState } from "react";
import axios from "axios";
import SEO from "../components/SEO";
import { PageHero, Icon } from "../components/ui-blocks";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { Phone, Mail, MapPin, Loader2 } from "lucide-react";
import { contact, company } from "../mock/mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initial = { name: "", email: "", phone: "", practice: "", providers: "", challenge: "", message: "" };

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: "Please add your name and email", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      setForm(initial);
      toast({ title: "Request received!", description: "Thanks — our team will reach out shortly about your free audit." });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again or email us directly.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact Us — Request a Free Insurance Revenue Risk Audit"
        description="Request your free Insurance Revenue Risk Audit. Contact VerifiAssist to identify preventable denial risk and reimbursement delays in your verification and claims workflows."
        path="/contact-us"
      />
      <PageHero eyebrow={contact.hero.eyebrow} title={contact.hero.title} subtitle={contact.hero.subtitle} />

      <section className="container-x py-16 sm:py-20">
        <div className="grid gap-10 lg:grid-cols-5">
          {/* Info */}
          <div className="lg:col-span-2">
            <h2 className="text-2xl font-bold text-brand-navy">Let's talk revenue</h2>
            <p className="mt-3 text-slate-600 leading-relaxed">Tell us a little about your practice and we'll prepare a focused review of where revenue may be slipping through the cracks.</p>
            <div className="mt-8 space-y-4">
              <a href={`tel:${company.phoneRaw}`} className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-colors hover:border-brand-200">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand-600"><Phone className="h-5 w-5" /></div>
                <div><div className="text-sm text-slate-500">Call us</div><div className="font-semibold text-brand-navy">{company.phone}</div></div>
              </a>
              <a href={`mailto:${company.email}`} className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-colors hover:border-brand-200">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand-600"><Mail className="h-5 w-5" /></div>
                <div><div className="text-sm text-slate-500">Email us</div><div className="font-semibold text-brand-navy">{company.email}</div></div>
              </a>
              <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand-600"><MapPin className="h-5 w-5" /></div>
                <div><div className="text-sm text-slate-500">Serving</div><div className="font-semibold text-brand-navy">Practices across the United States</div></div>
              </div>
            </div>
            <div className="mt-6 rounded-2xl bg-brand-navy p-6 text-brand-100">
              <div className="flex items-center gap-2 text-brand-teal"><Icon name="ShieldCheck" className="h-5 w-5" /><span className="text-sm font-semibold">HIPAA-conscious &amp; secure</span></div>
              <p className="mt-2 text-sm text-brand-100/80">Your information is handled with secure, HIPAA-conscious workflows.</p>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm">
              <h3 className="text-xl font-bold text-brand-navy">Request your free audit</h3>
              <div className="mt-6 grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name">Full name *</Label>
                  <Input id="name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Jane Doe" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="jane@practice.com" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input id="phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="(555) 123-4567" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="practice">Practice name</Label>
                  <Input id="practice" value={form.practice} onChange={(e) => update("practice", e.target.value)} placeholder="Bright Smiles Dental" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="providers">Number of providers</Label>
                  <Input id="providers" value={form.providers} onChange={(e) => update("providers", e.target.value)} placeholder="e.g. 3" />
                </div>
                <div className="space-y-2">
                  <Label>Biggest insurance challenge</Label>
                  <Select value={form.challenge} onValueChange={(v) => update("challenge", v)}>
                    <SelectTrigger><SelectValue placeholder="Select one" /></SelectTrigger>
                    <SelectContent>
                      {contact.challenges.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-5 space-y-2">
                <Label htmlFor="message">How can we help?</Label>
                <Textarea id="message" rows={4} value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Tell us about your current verification and claims workflow..." />
              </div>
              <Button type="submit" size="lg" disabled={submitting} className="mt-6 w-full bg-brand-600 hover:bg-brand-700">
                {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...</> : "Request my free audit"}
              </Button>
              <p className="mt-3 text-center text-xs text-slate-400">We'll never share your information. HIPAA-conscious &amp; secure.</p>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
